// src/pages/dashboard/index.jsx
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  Skeleton,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SalesTrend from "../../components/SalesTrend";
import InventoryOut from "../../components/InventoryOut";
import InventoryShare from "../../components/InventoryShare";
import StatBox from "../../components/StatBox";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTotalMonthlySales,
  getRecentTransactions,
  getTotalOutQuantity as fetchTotalOutQuantityApi,
} from "../../services/SaleService";
import { getOwingCustomers } from "../../services/CustomerService";
import { getTotalMonthlyExpenses } from "../../services/ExpenseService";
import { getTotalCustomers } from "../../services/CustomerService";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [monthlySales, setMonthlySales] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalOutQuantity, setTotalOutQuantity] = useState(0);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [owingCustomers, setOwingCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(null); // null => loading

  // UI-controlled cutoff (days) for owing customers (default 30)
  const [cutoffDays, setCutoffDays] = useState(30);
  const [cutoffInput, setCutoffInput] = useState("30");
  const [owingLoading, setOwingLoading] = useState(false);

  useEffect(() => {
    const fetchMonthlySales = async () => {
      try {
        const response = await getTotalMonthlySales(currentMonth, currentYear);
        setMonthlySales(response.data);
      } catch (error) {
        console.error("Error fetching monthly data: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMonthlySales();
  }, [currentMonth, currentYear]);

  useEffect(() => {
    const fetchMonthlyExpenses = async () => {
      try {
        const response = await getTotalMonthlyExpenses(
          currentMonth,
          currentYear,
        );
        setMonthlyExpenses(response.data);
      } catch (error) {
        console.error("Error fetching monthly data: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMonthlyExpenses();
  }, [currentMonth, currentYear]);

  useEffect(() => {
    const fetchTotalOutQuantities = async () => {
      try {
        const response = await fetchTotalOutQuantityApi();
        setTotalOutQuantity(response.data || 0);
      } catch (error) {
        console.error("Error fetching total out quantities: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTotalOutQuantities();
  }, []);

  useEffect(() => {
    const fetchRecentTransactions = async () => {
      try {
        const response = await getRecentTransactions(0, 5);

        // Sort by ID descending (Latest entry first) to match table behavior
        const sortedTransactions = [...response.data].sort(
          (a, b) => b.id - a.id,
        );

        setRecentTransactions(sortedTransactions);
      } catch (error) {
        console.error("Error fetching recent transactions: ", error);
      } finally {
        setRecentLoading(false);
      }
    };
    fetchRecentTransactions();
  }, []);

  useEffect(() => {
    const fetchOwingCustomers = async () => {
      setOwingLoading(true);
      try {
        const response = await getOwingCustomers(cutoffDays);
        setOwingCustomers(response.data);
      } catch (error) {
        console.error("Error fetching owing customers: ", error);
        setOwingCustomers([]);
      } finally {
        setOwingLoading(false);
      }
    };
    fetchOwingCustomers();
  }, [cutoffDays]);

  useEffect(() => {
    let mounted = true;
    const fetchTotal = async () => {
      try {
        const response = await getTotalCustomers();
        const val =
          response?.data && typeof response.data.totalCustomers === "number"
            ? response.data.totalCustomers
            : 0;
        if (mounted) setTotalCustomers(val);
      } catch (error) {
        console.error("Error fetching total customers: ", error);
        if (mounted) setTotalCustomers(0);
      }
    };
    fetchTotal();
    return () => {
      mounted = false;
    };
  }, []);

  const formatDateSafe = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return String(d);
    }
  };

  const applyCutoff = () => {
    const parsed = parseInt(cutoffInput, 10);
    if (isNaN(parsed) || parsed <= 0) {
      setCutoffDays(30);
      setCutoffInput("30");
    } else {
      setCutoffDays(parsed);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "clip",
        overflowY: "visible",
        boxSizing: "border-box",
        p: { xs: 2, sm: 3, md: 4 },
        "@keyframes slideInUp": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* HEADER */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{
          animation: "slideInUp 0.5s ease-out forwards",
        }}
      >
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
      </Box>

      {/* GRID & CHARTS */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="160px"
        gap="14px"
      >
        {/* ROW 1: KPI Stat Boxes */}
        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <StatBox
            title={
              loading ? (
                <Skeleton
                  animation="wave"
                  width={80}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                  }}
                />
              ) : (
                `${totalOutQuantity.toLocaleString()}`
              )
            }
            subtitle="Inventory Distributed (YTD)"
            icon={
              <Inventory2Icon
                sx={{ color: colors.greenAccent[600], fontSize: "48px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <StatBox
            title={
              loading ? (
                <Skeleton
                  animation="wave"
                  width={80}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                  }}
                />
              ) : (
                `₦${monthlySales.toLocaleString()}`
              )
            }
            subtitle="Total Monthly Sales"
            icon={
              <PointOfSaleIcon
                sx={{ color: colors.greenAccent[600], fontSize: "48px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <StatBox
            title={
              totalCustomers === null ? (
                <Skeleton
                  animation="wave"
                  width={80}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                  }}
                />
              ) : (
                `${totalCustomers.toLocaleString()}`
              )
            }
            subtitle="Total Customers"
            icon={
              <PersonAddIcon
                sx={{ color: colors.greenAccent[600], fontSize: "48px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.15s",
          }}
        >
          <StatBox
            title={
              loading ? (
                <Skeleton
                  animation="wave"
                  width={80}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                  }}
                />
              ) : (
                `₦${monthlyExpenses.toLocaleString()}`
              )
            }
            subtitle="Total Monthly Expenses"
            icon={
              <MonetizationOnIcon
                sx={{ color: colors.greenAccent[600], fontSize: "48px" }}
              />
            }
          />
        </Box>

        {}
        {/* ROW 2 & 3: Recent Transactions */}
        <Box
          gridColumn={{ xs: "span 12", lg: "span 6" }}
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.3s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Recent Sales
            </Typography>
            <Typography
              variant="body2"
              color={colors.blueAccent[500]}
              onClick={() => navigate("/sales")}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View All
            </Typography>
          </Box>

          <Box flex="1" sx={{ overflowX: "hidden", overflowY: "hidden" }}>
            <Box
              width="100%"
              height="100%"
              display="flex"
              flexDirection="column"
            >
              {/* Table header */}
              <Box
                display="flex"
                p="12px 24px"
                borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
                backgroundColor={
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.01)"
                }
              >
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1.5 }}
                >
                  Customer & Ref
                </Typography>
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1, textAlign: "left" }}
                >
                  Date
                </Typography>
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1, textAlign: "right" }}
                >
                  Amount
                </Typography>
              </Box>

              {/* Table Body */}
              <Box
                overflow="auto"
                flex="1"
                sx={{
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {recentLoading ? (
                  <Box p="10px 24px">
                    {[...Array(5)].map((_, i) => (
                      <Box
                        key={i}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        p="14px 0"
                        borderBottom={
                          i === 4
                            ? "none"
                            : `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.03)"
                                  : "rgba(0,0,0,0.03)"
                              }`
                        }
                      >
                        <Skeleton
                          variant="rounded"
                          width="35%"
                          height={32}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "4px",
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="20%"
                          height={20}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "4px",
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="15%"
                          height={28}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "20px",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : recentTransactions && recentTransactions.length > 0 ? (
                  recentTransactions.map((transaction, i) => (
                    <Box
                      key={`${transaction.id}-${i}`}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      p="14px 24px"
                      borderBottom={
                        i === recentTransactions.length - 1
                          ? "none"
                          : `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"}`
                      }
                      sx={{
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.02)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      {/* Left column: Customer name + Reference (id) */}
                      <Box sx={{ flex: 1.5 }}>
                        <Typography
                          color={colors.gray[100]}
                          variant="body1"
                          fontWeight="600"
                          mb="2px"
                        >
                          {transaction.customerName}
                        </Typography>
                        <Typography
                          color={colors.gray[400]}
                          variant="caption"
                          sx={{ letterSpacing: "0.5px" }}
                        >
                          #TRX-{transaction.id}
                        </Typography>
                      </Box>

                      {/* Middle column: Date */}
                      <Typography
                        color={colors.gray[300]}
                        variant="body2"
                        sx={{ flex: 1, textAlign: "left", fontWeight: 500 }}
                      >
                        {typeof formatDateSafe === "function"
                          ? formatDateSafe(transaction.date)
                          : transaction.date}
                      </Typography>

                      {/* Right column: Amount */}
                      <Box sx={{ flex: 1, textAlign: "right" }}>
                        <Box
                          component="span"
                          p="6px 12px"
                          borderRadius="20px"
                          backgroundColor={
                            theme.palette.mode === "dark"
                              ? "rgba(76, 206, 172, 0.1)"
                              : "rgba(5, 150, 105, 0.1)"
                          }
                          color={
                            theme.palette.mode === "dark"
                              ? colors.greenAccent[400]
                              : colors.greenAccent[600]
                          }
                          fontWeight="700"
                          fontSize="0.8rem"
                          sx={{ display: "inline-block" }}
                        >
                          ₦
                          {transaction.totalBalance != null
                            ? Number(transaction.totalBalance).toLocaleString()
                            : "0"}
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    minHeight="220px"
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.02)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "inset 0 2px 10px rgba(0,0,0,0.2)"
                            : "inset 0 2px 10px rgba(0,0,0,0.05)",
                      }}
                    >
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={
                          theme.palette.mode === "dark"
                            ? colors.blueAccent[600]
                            : colors.blueAccent[500]
                        }
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                    </Box>
                    <Typography
                      color={colors.gray[100]}
                      variant="h5"
                      fontWeight="600"
                      mb="4px"
                    >
                      No Sales Yet
                    </Typography>
                    <Typography color={colors.gray[400]} variant="body2">
                      Recent transactions will appear here.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {}
        <Box
          gridColumn={{ xs: "span 12", lg: "span 6" }}
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.3s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Inventory Out (By Product)
            </Typography>
          </Box>

          <Box flex="1" sx={{ overflow: "hidden", p: 2 }}>
            <InventoryOut isDashboard={true} />
          </Box>
        </Box>

        <Box
          gridColumn="span 12"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.45s",
          }}
        >
          {/* Owing Customers header */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={{ xs: 2, sm: 0 }}
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Owing Customers
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              width={{ xs: "100%", sm: "auto" }}
            >
              {/* Cutoff control */}
              <TextField
                size="small"
                label="Cutoff days"
                value={cutoffInput}
                onChange={(e) => setCutoffInput(e.target.value)}
                sx={{
                  flex: { xs: 1, sm: "0 0 auto" },
                  width: { xs: "auto", sm: 120 },
                  minWidth: 0,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "#ffffff",
                    borderRadius: "8px",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "inset 0 1px 3px rgba(0,0,0,0.2)"
                        : "0 1px 4px rgba(0,0,0,0.04)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "#f8fafc",
                    },
                    "&.Mui-focused": {
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? `0 0 0 2px rgba(99, 102, 241, 0.3)`
                          : `0 0 0 2px rgba(79, 70, 229, 0.25)`,
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                  },
                }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={applyCutoff}
                sx={{
                  flexShrink: 0,
                  backgroundColor: colors.blueAccent[600],
                  color: "#ffffff",
                  fontWeight: 600,
                  borderRadius: "8px",
                  padding: "6px 18px",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 4px 10px rgba(0,0,0,0.3)"
                      : "0 4px 12px rgba(79, 70, 229, 0.2)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: colors.blueAccent[500],
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 6px 14px rgba(0,0,0,0.4)"
                        : "0 6px 16px rgba(79, 70, 229, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                    boxShadow: "none",
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>

          <Box flex="1" sx={{ overflowX: "hidden", overflowY: "hidden" }}>
            <Box
              width="100%"
              height="100%"
              display="flex"
              flexDirection="column"
            >
              {/* Table header */}
              <Box
                display="flex"
                p="12px 24px"
                borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
                backgroundColor={
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.01)"
                }
              >
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1 }}
                >
                  Name
                </Typography>
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1, textAlign: "center" }}
                >
                  Days Owing
                </Typography>
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1, textAlign: "center" }}
                >
                  Amount Owing
                </Typography>
                <Typography
                  color={colors.gray[400]}
                  variant="caption"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  fontWeight="700"
                  sx={{ flex: 1, textAlign: "right" }}
                >
                  Due Date
                </Typography>
              </Box>

              {/* Table Body */}
              <Box
                flex="1"
                sx={{
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                {owingLoading ? (
                  <Box p="10px 24px">
                    {[...Array(5)].map((_, i) => (
                      <Box
                        key={i}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        p="14px 0"
                        borderBottom={
                          i === 4
                            ? "none"
                            : `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.03)"
                                  : "rgba(0,0,0,0.03)"
                              }`
                        }
                      >
                        <Skeleton
                          variant="rounded"
                          width="25%"
                          height={20}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "4px",
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="15%"
                          height={20}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "4px",
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="20%"
                          height={20}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "4px",
                          }}
                        />
                        <Skeleton
                          variant="rounded"
                          width="15%"
                          height={20}
                          sx={{
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.04)",
                            borderRadius: "4px",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : owingCustomers && owingCustomers.length > 0 ? (
                  owingCustomers.map((customer, index) => {
                    const backendOverdue =
                      customer.isOverdue ?? customer.overdue ?? null;

                    const daysOwingNum =
                      customer.daysOwing != null
                        ? Number(customer.daysOwing)
                        : null;
                    const earliest = customer.earliestDueDate
                      ? new Date(customer.earliestDueDate)
                      : null;
                    const cutoffDate = new Date();
                    cutoffDate.setDate(cutoffDate.getDate() - cutoffDays);
                    const locallyOverdue =
                      (daysOwingNum != null && daysOwingNum >= cutoffDays) ||
                      (earliest != null && earliest <= cutoffDate);

                    const overdue =
                      backendOverdue != null ? backendOverdue : locallyOverdue;

                    return (
                      <Box
                        key={index}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        p="14px 24px"
                        borderBottom={
                          index === owingCustomers.length - 1
                            ? "none"
                            : `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"}`
                        }
                        sx={{
                          transition: "background-color 0.2s ease",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.02)"
                                : "rgba(0,0,0,0.02)",
                          },
                        }}
                      >
                        <Typography
                          color={colors.gray[100]}
                          variant="body1"
                          fontWeight="600"
                          sx={{ flex: 1 }}
                        >
                          {customer.customerName || "N/A"}
                        </Typography>
                        <Typography
                          color={colors.gray[300]}
                          variant="body2"
                          sx={{ flex: 1, textAlign: "center", fontWeight: 500 }}
                        >
                          {customer.daysOwing != null
                            ? `${customer.daysOwing} days`
                            : "-"}
                        </Typography>
                        <Typography
                          color={colors.gray[300]}
                          variant="body2"
                          sx={{ flex: 1, textAlign: "center", fontWeight: 500 }}
                        >
                          ₦
                          {customer.amountOwed != null
                            ? customer.amountOwed.toLocaleString()
                            : "0"}
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            textAlign: "right",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <Typography
                            color={colors.gray[300]}
                            variant="body2"
                            fontWeight="500"
                          >
                            {earliest ? formatDateSafe(earliest) : "-"}
                          </Typography>
                          {overdue && (
                            <Box
                              component="span"
                              p="4px 8px"
                              borderRadius="12px"
                              backgroundColor={
                                theme.palette.mode === "dark"
                                  ? "rgba(244, 63, 94, 0.15)"
                                  : "rgba(225, 29, 72, 0.1)"
                              }
                              color={
                                theme.palette.mode === "dark"
                                  ? colors.redAccent[400]
                                  : colors.redAccent[500]
                              }
                              fontSize="0.7rem"
                              fontWeight="700"
                              letterSpacing="0.5px"
                              sx={{ display: "inline-block" }}
                            >
                              OVERDUE
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    minHeight="220px"
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.02)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "inset 0 2px 10px rgba(0,0,0,0.2)"
                            : "inset 0 2px 10px rgba(0,0,0,0.05)",
                      }}
                    >
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={
                          theme.palette.mode === "dark"
                            ? colors.greenAccent[600]
                            : colors.greenAccent[500]
                        }
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </Box>
                    <Typography
                      color={colors.gray[100]}
                      variant="h5"
                      fontWeight="600"
                      mb="4px"
                    >
                      All clear!
                    </Typography>
                    <Typography color={colors.gray[400]} variant="body2">
                      No customers owing ≥ {cutoffDays} days.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* TOP-RIGHT: InventoryOut (swapped into top-right). width = remaining 5 cols */}

        {/* UNDER TOP-RIGHT: Recent Sales placed under InventoryOut (span 5) */}
        <Box
          gridColumn={{ xs: "span 12", lg: "span 5" }}
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.6s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Inventory Share
            </Typography>
          </Box>

          <Box
            flex="1"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              p: 2,
            }}
          >
            <InventoryShare isDashboard={true} />
          </Box>
        </Box>

        <Box
          gridColumn={{ xs: "span 12", lg: "span 7" }}
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          border={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`}
          display="flex"
          flexDirection="column"
          boxShadow={
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)"
          }
          overflow="hidden"
          sx={{
            opacity: 0,
            animation: "slideInUp 0.5s ease-out forwards",
            animationDelay: "0.6s",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
            padding="20px 24px"
          >
            <Typography color={colors.gray[100]} variant="h4" fontWeight="700">
              Sales Trend
            </Typography>
          </Box>

          <Box
            flex="1"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              width: "100%",
              height: "100%",
              p: 2,
            }}
          >
            <SalesTrend isDashboard={true} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
